import { Box, Button, Link, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { DeadlineRow } from "./DeadlineRow";
import {
  bffApi,
  DeadlineKind,
  useListMyUpcomingDeadlinesQuery,
  type UpcomingDeadlineDto,
} from "../../../autogen/bff-api";
import { KeyboardScrollBox } from "../../../common/KeyboardScrollBox";
import { useApiError } from "../../../common/errors/useApiError";
import { urls } from "../../../urls";
import { URGENCY_COLOR, deadlineUrgency } from "../deadlineUrgency";
import { useHomeLabel } from "../useHomeLabel";
import { PanelBody, PanelCard, PanelFigure, PanelHeader } from "./HomePanel";

const OPENING = { upcomingLimit: 5, overdueLimit: 3, withinDays: 90, overdueDays: 30 };
const PAGE_SIZE = 5;

interface LoadedPages {
  earlier: UpcomingDeadlineDto[];
  later: UpcomingDeadlineDto[];
  nextEarlier?: string | null;
  nextLater?: string | null;
}

const isOverdueTask = (deadline: UpcomingDeadlineDto) =>
  deadline.kind === DeadlineKind.ContractTask && deadlineUrgency(deadline.dueAt) === "overdue";

export const UpcomingDeadlinesPanel = () => {
  const { t } = useTranslation();
  const { trigger: reportError } = useApiError();
  const { data, isLoading, isError } = useListMyUpcomingDeadlinesQuery(OPENING);
  const [fetchPage, { isFetching }] = bffApi.endpoints.listMyUpcomingDeadlines.useLazyQuery();
  const [pages, setPages] = useState<LoadedPages>({ earlier: [], later: [] });
  const inFlight = useRef({ earlier: false, later: false });

  const opening = data?.deadlines ?? [];
  const deadlines = [
    ...opening.filter(isOverdueTask),
    ...pages.earlier,
    ...opening.filter((deadline) => !isOverdueTask(deadline)),
    ...pages.later,
  ];
  const moreOverdueTasks = data?.moreOverdueTasks ?? 0;
  const nextEarlier = pages.nextEarlier === undefined ? data?.nextOverduePosition : pages.nextEarlier;
  const nextLater = pages.nextLater === undefined ? data?.nextUpcomingPosition : pages.nextLater;

  const showEarlier = async () => {
    if (!nextEarlier || inFlight.current.earlier) return;
    inFlight.current.earlier = true;
    try {
      const page = await fetchPage({
        overdueAfter: nextEarlier,
        overdueLimit: PAGE_SIZE,
        overdueDays: OPENING.overdueDays,
        upcomingLimit: 0,
      }).unwrap();
      setPages((loaded) => ({
        ...loaded,
        earlier: [...page.deadlines, ...loaded.earlier],
        nextEarlier: page.nextOverduePosition ?? null,
      }));
    } catch (e) {
      reportError(e);
    } finally {
      inFlight.current.earlier = false;
    }
  };
  const showLater = async () => {
    if (!nextLater || inFlight.current.later) return;
    inFlight.current.later = true;
    try {
      const page = await fetchPage({
        upcomingAfter: nextLater,
        upcomingLimit: PAGE_SIZE,
        withinDays: OPENING.withinDays,
        overdueLimit: 0,
      }).unwrap();
      setPages((loaded) => ({
        ...loaded,
        later: [...loaded.later, ...page.deadlines],
        nextLater: page.nextUpcomingPosition ?? null,
      }));
    } catch (e) {
      reportError(e);
    } finally {
      inFlight.current.later = false;
    }
  };

  return (
    <PanelCard>
      <PanelHeader titleKey="Deadlines" icon={FiClock} />
      <PastDeadlineFigure count={isError ? undefined : (data?.overdueCount ?? undefined)} isLoading={isLoading} />
      {nextEarlier && <MoreButton labelKey="Show earlier" onClick={() => void showEarlier()} isLoading={isFetching} />}
      <PanelBody
        isLoading={isLoading}
        isError={isError}
        isEmpty={deadlines.length === 0}
        emptyKey={emptyStateKey((data?.overdueCount ?? 0) > 0, !!nextEarlier, !!nextLater)}
      >
      {/*
        Evenly spaced rather than spaced by time: true intervals would leave weeks of empty rail
        between two dates and crowd three that fall in the same afternoon. The rail carries the order,
        the colour carries the urgency, and the date itself says how far off it is.
      */}
      <KeyboardScrollBox px="4" py="4" maxH="26rem" overflowY="auto">
        {deadlines.map((deadline, index) => {
          const isLast = index === deadlines.length - 1;
          return (
            <DeadlineRow
              key={`${deadline.subjectId}-${deadline.kind}-${deadline.dueAt}`}
              deadline={deadline}
              pl="6"
              pb={isLast ? "0" : "5"}
            >
              {!isLast && (
                <Box position="absolute" left="4px" top="16px" bottom="0" w="2px" bg="smBorder" aria-hidden />
              )}
              <Box
                position="absolute"
                left="0"
                top="6px"
                w="10px"
                h="10px"
                rounded="full"
                bg={URGENCY_COLOR[deadlineUrgency(deadline.dueAt)]}
                aria-hidden
              />
            </DeadlineRow>
          );
        })}
      </KeyboardScrollBox>
      {moreOverdueTasks > 0 && (
        <Text px="4" pb="3" fontSize="sm" color="smError">
          <Link as={RouterLink} to={urls.app.contracts.tasks} color="smError" textDecoration="underline">
            {t("{{count}} more overdue tasks: see all your contract tasks", { count: moreOverdueTasks })}
          </Link>
        </Text>
      )}
      </PanelBody>
      {nextLater && <MoreButton labelKey="Show later" onClick={() => void showLater()} isLoading={isFetching} />}
    </PanelCard>
  );
};

const MoreButton = ({
  labelKey,
  onClick,
  isLoading,
}: {
  labelKey: string;
  onClick: () => void;
  isLoading: boolean;
}) => {
  const homeLabel = useHomeLabel();
  return (
    <Button
      variant="ghost"
      size="sm"
      w="full"
      rounded="none"
      color="smMuted"
      fontWeight="normal"
      borderTop="1px solid"
      borderColor="smBorder"
      isLoading={isLoading}
      onClick={onClick}
    >
      {homeLabel(labelKey)}
    </Button>
  );
};

const emptyStateKey = (someHavePassed: boolean, earlierCanBeShown: boolean, laterCanBeShown: boolean) => {
  if (earlierCanBeShown && laterCanBeShown) {
    return "No deadlines in the last 30 days or the next 90 days. Older deadlines that have passed are under «Show earlier», and later ones under «Show later».";
  }
  if (earlierCanBeShown) {
    return "No deadlines in the last 30 days or the next 90 days. Older deadlines that have passed are under «Show earlier».";
  }
  if (laterCanBeShown) {
    return "No deadlines in the last 30 days or the next 90 days. Later deadlines are under «Show later».";
  }
  return someHavePassed ? "No deadlines in the last 30 days or the next 90 days" : "No deadlines coming up";
};

const PastDeadlineFigure = ({ count, isLoading }: { count?: number; isLoading: boolean }) => {
  const homeLabel = useHomeLabel();
  return (
    <PanelFigure
      value={count ?? "–"}
      label={homeLabel("Past their deadline")}
      qualifier={homeLabel("Deadlines and tasks gone by")}
      isLoading={isLoading}
      isAlert={!!count}
      divider="bottom"
    />
  );
};
